import React from 'react';
import { X, Trash2, Plus } from 'lucide-react';
import { getCurrencySymbol } from './invoiceHelpers';

// Attach Expenses Modal
export const AttachExpensesModal = ({
  isOpen,
  onClose,
  invoice,
  caseExpenses,
  selectedExpenses,
  customExpenses,
  attachingExpenses,
  handleToggleExpense,
  addCustomExpense,
  removeCustomExpense,
  updateCustomExpense,
  onSubmit
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#1a1a2e] rounded-xl border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 sticky top-0 bg-[#1a1a2e] z-10">
          <h3 className="text-xl font-semibold text-white">Attach Expenses</h3>
          <button
            onClick={onClose}
            disabled={attachingExpenses}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Case Expenses Section */}
          {caseExpenses.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-4">Case Expenses</h4>
              <p className="text-sm text-gray-400 mb-4">
                Select expenses from the case: {invoice.selectedCase?.caseNo} - {invoice.selectedCase?.petitionerName} VS {invoice.selectedCase?.respondentName}
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {caseExpenses.map((expense) => (
                  <label
                    key={expense.id}
                    className="flex items-center justify-between p-3 bg-[#0f0f23] border border-gray-700 rounded-lg hover:bg-[#252540] cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedExpenses.includes(expense.id)}
                        onChange={() => handleToggleExpense(expense.id)}
                        className="w-4 h-4 text-blue-500 bg-[#0f0f23] border-gray-700 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-white text-sm">{expense.type}</div>
                        {expense.description && (
                          <div className="text-gray-400 text-xs">{expense.description}</div>
                        )}
                        <div className="text-gray-500 text-xs">{expense.date}</div>
                      </div>
                    </div>
                    <div className="text-white font-medium">
                      {getCurrencySymbol(invoice.currency)} {parseFloat(expense.amount).toFixed(2)}
                    </div>
                  </label>
                ))}
              </div>
              {selectedExpenses.length > 0 && (
                <div className="mt-3 text-sm text-blue-400">
                  {selectedExpenses.length} expense(s) selected
                </div>
              )}
            </div>
          )}

          {/* Custom Expenses Section */}
          <div>
            <h4 className="text-white font-semibold mb-4">
              {caseExpenses.length > 0 ? "Or Add Custom Expenses" : "Add Expenses"}
            </h4>
            <div className="space-y-3">
              {customExpenses.map((expense) => (
                <div key={expense.id} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      value={expense.description}
                      onChange={(e) => updateCustomExpense(expense.id, "description", e.target.value)}
                      placeholder="Enter description (e.g., Court fees, Travel)"
                      className="w-full px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#C99F4A] transition-colors"
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={expense.amount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
                        updateCustomExpense(expense.id, "amount", val);
                      }}
                      placeholder="Enter amount"
                      className="flex-1 px-4 py-2 bg-[#0f0f23] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#C99F4A] transition-colors"
                    />
                    {customExpenses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCustomExpense(expense.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addCustomExpense}
              className="mt-3 flex items-center gap-1 px-3 py-1 text-sm text-[#C99F4A] hover:text-[#D19B15] font-medium transition-colors"
            >
              <Plus size={16} />
              Add Another Expense
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700 sticky bottom-0 bg-[#1a1a2e]">
          <button
            onClick={onClose}
            disabled={attachingExpenses}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={attachingExpenses}
            className="px-6 py-2 bg-[#C99F4A] hover:bg-[#D19B15] text-[#0f0f23] rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {attachingExpenses ? "Attaching..." : "Attach Expenses"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachExpensesModal;
